import { MajorMenu, PathRouteProps, PluginContextType } from "bimui";
import { useCallback, useState } from "react";
import localStore from "./local-store";
import { Major } from "@/declare/interface/project/major";

type PanelDict = { [key: string]: (props?: PluginContextType) => React.JSX.Element };
type MenuTabsDict = { [key: string]: string[] };
/**
 * 注册存储定义
 */
export interface RegisterStatus {

    majors?: Major[]; // 注册的专业
    setMajors?: React.Dispatch<React.SetStateAction<Major[]>>; // 设置专业
    registMajors?: (majors: Major[]) => void // 注册专业

    menus?: MajorMenu[]; // 注册的菜单栏
    setMenus?: React.Dispatch<React.SetStateAction<MajorMenu[]>>; // 设置菜单栏
    registMenus?: (menus: MajorMenu[]) => void // 注册菜单

    libMenus?: MajorMenu[]; // 注册的专业库菜单栏
    setLibMenus?: React.Dispatch<React.SetStateAction<MajorMenu[]>>; // 设置专业库菜单栏
    registLibMenus?: (menus: MajorMenu[]) => void // 注册专业库菜单

    systemLibMenus?: MajorMenu[]; // 注册的系统库菜单栏
    setSystemLibMenus?: React.Dispatch<React.SetStateAction<MajorMenu[]>>; // 设置系统库菜单栏
    registSystemLibMenus?: (menus: MajorMenu[]) => void // 注册系统库菜单

    routes?: PathRouteProps[]; // 注册的内容区
    setRoutes?: React.Dispatch<React.SetStateAction<PathRouteProps[]>>; // 设置内容区
    registRoutes?: (routes: PathRouteProps[]) => void // 注册内容区

    sidebarPanels: PanelDict, // 侧边栏
    setSidebarPanels: React.Dispatch<React.SetStateAction<PanelDict>>, // 设置侧边栏
    registSidebarPanels?: (panelDict: PanelDict) => void // 注册侧边栏

    toolPanels: PanelDict, // 工具面板
    setToolPanels: React.Dispatch<React.SetStateAction<PanelDict>>, // 设置工具面板
    registToolPanels?: (panelDict: PanelDict) => void // 注册工具面板

    currentActivedSidebarKey: string, // 当前激活的侧边栏key
    // setActiveSidebarKey: React.Dispatch<React.SetStateAction<string>>, // 设置当前激活的侧边栏key
    activeSidebar: (key: string) => void // 激活侧边栏

    currentActivedToolKey: string, // 当前激活的工具面板key
    // setActiveToolKey: React.Dispatch<React.SetStateAction<string>>, // 设置当前激活的工具面板key
    activeTool: (key: string) => void // 激活工具面板

    isSidebarCollapsed: boolean; // 当前侧边栏是否是收起状态
    setSidebarCollapsed: React.Dispatch<React.SetStateAction<boolean>>; // 设置侧边栏状态的方法
}

/**
 * 注册存储
 * @returns 
 */
export function useRegisterService() {
    const [majors, setMajors] = useState<Major[]>([]); // 专业
    const [menus, setMenus] = useState<MajorMenu[]>([]); // 导航菜单
    const [libMenus, setLibMenus] = useState<MajorMenu[]>([]); // 专业库导航菜单
    const [systemLibMenus, setSystemLibMenus] = useState<MajorMenu[]>([]); // 系统库导航菜单
    const [sidebarPanels, setSidebarPanels] = useState<PanelDict>({}); // 侧边栏
    const [toolPanels, setToolPanels] = useState<PanelDict>({}); // 工具面板
    const [routes, setRoutes] = useState<PathRouteProps[]>([]); // 路由内容区

    const [currentActivedSidebarKey, setActiveSidebarKey] = useState<string>(localStore.getActiveSidebarKey()); // 当前激活的侧边栏
    const [currentActivedToolKey, setActiveToolKey] = useState<string>(); // 当前激活的工具面板
    const [isSidebarCollapsed, setSidebarCollapsed] = useState(false); // 侧边栏是否收起

    // 注册专业
    const registMajors = useCallback((registMajors: Major[]) => {
        setMajors([
            ...majors,
            ...registMajors,
        ])
    }, [majors]);

    // 注册菜单
    const registMenus = useCallback((registMenus: MajorMenu[]) => {
        setMenus([
            ...menus,
            ...registMenus,
        ])
    }, [menus]);

    // 注册专业库菜单
    const registLibMenus = useCallback((registMenus: MajorMenu[]) => {
        setLibMenus([
            ...libMenus,
            ...registMenus,
        ])
    }, [libMenus]);

    // 注册系统库菜单
    const registSystemLibMenus = useCallback((registMenus: MajorMenu[]) => {
        setSystemLibMenus([
            ...systemLibMenus,
            ...registMenus,
        ])
    }, [systemLibMenus])

    // 注册内容区
    const registRoutes = useCallback((registRoutes: PathRouteProps[]) => {
        setRoutes([
            ...routes,
            ...registRoutes,
        ])
    }, [routes]);

    // 注册侧边栏
    const registSidebarPanels = useCallback((panelDict: PanelDict) => {
        setSidebarPanels({
            ...sidebarPanels,
            ...panelDict,
        })
    }, [sidebarPanels])

    // 注册工具面板
    const registToolPanels = useCallback((panelDict: PanelDict) => {
        setToolPanels({
            ...toolPanels,
            ...panelDict,
        })
    }, [toolPanels])

    // 激活侧边栏
    const activeSidebar = useCallback((key: string) => {
        setActiveSidebarKey(key);
        localStore.setActiveSidebarKey(key);
    }, [sidebarPanels])

    // 激活工具面板
    const activeTool = useCallback((key: string) => {
        setActiveToolKey(key);
        localStore.setActiveToolKey(key);
    }, [toolPanels])

    return {
        majors,
        setMajors,
        registMajors,
        menus,
        setMenus,
        registMenus,
        libMenus,
        setLibMenus,
        registLibMenus,
        systemLibMenus,
        setSystemLibMenus,
        registSystemLibMenus,
        routes,
        setRoutes,
        registRoutes,
        sidebarPanels,
        setSidebarPanels,
        registSidebarPanels,
        toolPanels,
        setToolPanels,
        registToolPanels,
        currentActivedSidebarKey,
        // setActiveSidebarKey,
        activeSidebar,
        currentActivedToolKey,
        // setActiveToolKey,
        activeTool,
        isSidebarCollapsed,
        setSidebarCollapsed,
    } as RegisterStatus;
}
